/**
 * DynamicIcon — renders one icon by its `@mui/icons-material` component
 * name (a module's `icon` field, e.g. from ModuleConfigPage.tsx's module
 * list). Added 2026-09-04, direct instruction: "These icons should also be
 * displayed on anoorehr too" -- mirrors et-console-staging's own
 * `DynamicIcon` (`src/components/primitives/IconPickerField.tsx`), same
 * reasoning: Console lets an operator pick from the *entire* ~2,000-icon
 * MUI set (`IconPickerField`), but this page's own `ICON_MAP` was a
 * hand-maintained lookup covering only the ~20 icon names AnooreHR's
 * original, locally-seeded modules happened to use. Any module created or
 * re-iconed on Console with a different icon name silently fell through to
 * a generic lock icon instead.
 *
 * Loaded via a dynamic `import('@mui/icons-material')`, not a static
 * `import * as Icons`, for the same bundle-size reason Console's own
 * version documents: the full package is ~2,000 icons, and a static
 * namespace import would ship all of them to every user on every page load
 * instead of only the (still admin/settings-only, still relatively rare)
 * pages that actually render a dynamic module icon. Cached at module scope
 * so it's fetched at most once per session, however many DynamicIcon
 * instances end up on the page.
 */

import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';

type IconComponent = ComponentType<SvgIconProps>;
type IconModule = Record<string, IconComponent>;

let iconModulePromise: Promise<IconModule> | null = null;

function loadAllIcons(): Promise<IconModule> {
  if (!iconModulePromise) {
    iconModulePromise = import('@mui/icons-material').then((mod) => {
      const out: IconModule = {};
      for (const [name, value] of Object.entries(mod)) {
        // The package's only non-icon export is its own `default` -- an
        // object, not a component. Everything else is a real icon.
        if (name !== 'default' && (typeof value === 'object' || typeof value === 'function') && value !== null) {
          out[name] = value as unknown as IconComponent;
        }
      }
      return out;
    });
  }
  return iconModulePromise;
}

export function DynamicIcon({
  name,
  fontSize = 'small',
  fallback,
  sx,
}: {
  name: string | null | undefined;
  fontSize?: SvgIconProps['fontSize'];
  /** Rendered once the icon set has loaded but `name` isn't in it (e.g. a
   * typo, or an icon renamed/removed upstream) -- omit to render nothing
   * in that case, which reads more honestly in a dense list than a
   * generic placeholder that looks like it might just be loading. */
  fallback?: IconComponent;
  sx?: SvgIconProps['sx'];
}) {
  const [icons, setIcons] = useState<IconModule | null>(null);

  useEffect(() => {
    if (!name) return;
    let cancelled = false;
    loadAllIcons().then((mod) => {
      if (!cancelled) setIcons(mod);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (!name) return null;
  const Icon = icons?.[name] ?? (icons ? fallback : undefined);
  if (!Icon) return null;
  return <Icon fontSize={fontSize} sx={sx} />;
}
